/* GlobalPhraseExpander — listens to input/textarea/contenteditable globally
 * and replaces triggers with their expansion when followed by space/tab/enter/punctuation.
 *
 * Triggered on keydown so we can intercept the trigger key and prevent default if expanded.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { loadPhrases, expandVariables, findMatchingPhrase } from '../utils/phraseSnippets';

const DELIMITER_KEYS = new Set([' ', 'Tab', 'Enter']);
// Disabled flag — let user toggle on/off
const ENABLED_KEY = 'phrases_enabled_v1';

function isEditable(el) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input') {
    const t = (el.type || '').toLowerCase();
    return ['text', 'search', 'email', 'url', ''].includes(t);
  }
  if (tag === 'textarea') return true;
  if (el.isContentEditable) return true;
  return false;
}

function getValue(el) {
  if (el.isContentEditable) return el.innerText || '';
  return el.value || '';
}

function getCaret(el) {
  if (el.isContentEditable) {
    const sel = window.getSelection?.();
    if (!sel || sel.rangeCount === 0) return 0;
    const r = sel.getRangeAt(0);
    const pre = r.cloneRange();
    pre.selectNodeContents(el);
    pre.setEnd(r.endContainer, r.endOffset);
    return pre.toString().length;
  }
  return el.selectionStart ?? 0;
}

function setSelectionRange(el, start, end) {
  try {
    if (el.isContentEditable) {
      // For contenteditable, set selection at character offset
      const sel = window.getSelection();
      const range = document.createRange();
      const findPos = (node, offset) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (offset <= node.length) return [node, offset];
          return [null, offset - node.length];
        }
        for (const c of node.childNodes) {
          const [n, rem] = findPos(c, offset);
          if (n) return [n, rem];
          offset = rem;
        }
        return [null, offset];
      };
      const [n1, o1] = findPos(el, start);
      const [n2, o2] = findPos(el, end);
      if (n1 && n2) {
        range.setStart(n1, o1);
        range.setEnd(n2, o2);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    } else {
      el.setSelectionRange(start, end);
    }
  } catch {/**/}
}

function replaceTextRange(el, start, end, replacement) {
  if (el.isContentEditable) {
    setSelectionRange(el, start, end);
    document.execCommand('insertText', false, replacement);
    return;
  }
  // React controlled components ignore direct `.value` writes — use native setter
  const proto = el.tagName?.toLowerCase() === 'textarea'
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  const value = el.value || '';
  const newValue = value.slice(0, start) + replacement + value.slice(end);
  if (nativeSetter) {
    nativeSetter.call(el, newValue);
  } else {
    el.value = newValue;
  }
  const newCaret = start + replacement.length;
  try { el.setSelectionRange(newCaret, newCaret); } catch {/**/}
  // Dispatch a proper InputEvent so React's onChange fires
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

export default function GlobalPhraseExpander() {
  useEffect(() => {
    let phrases = loadPhrases();
    let enabled = true;
    try { enabled = localStorage.getItem(ENABLED_KEY) !== '0'; } catch {/**/}
    const reload = () => { phrases = loadPhrases(); };
    const onEnabledChange = () => {
      try { enabled = localStorage.getItem(ENABLED_KEY) !== '0'; } catch {/**/}
    };
    window.addEventListener('phrases-changed', reload);
    window.addEventListener('phrases-enabled-changed', onEnabledChange);

    const onKey = (e) => {
      if (!enabled) return;
      if (!DELIMITER_KEYS.has(e.key)) return;
      const el = e.target;
      if (!isEditable(el)) return;
      // Skip inside narrator panel input
      if (el.closest?.('[data-testid="narrator-modal"]')) return;
      // Skip inside phrase-editor itself (so you can type triggers in your snippets)
      if (el.closest?.('[data-testid="phrase-editor"]')) return;

      const caret = getCaret(el);
      const fullText = getValue(el);
      const before = fullText.slice(0, caret);

      const match = findMatchingPhrase(before, phrases);
      if (!match) return;

      // Replace trigger with expansion
      const expansion = expandVariables(match.phrase.expansion);
      // For Enter/Tab we don't want to also insert the delimiter — just trigger expansion
      const insertDelimiter = (e.key === ' '); // only keep space, drop tab/enter
      const replacement = expansion + (insertDelimiter ? ' ' : '');
      e.preventDefault();
      replaceTextRange(el, match.matchStart, caret, replacement);

      // Update usage stats
      try {
        const updated = phrases.map((p) =>
          p.id === match.phrase.id
            ? { ...p, used_count: (p.used_count || 0) + 1, last_used: new Date().toISOString() }
            : p
        );
        localStorage.setItem('phrase_snippets_v1', JSON.stringify(updated));
        phrases = updated;
      } catch {/**/}

      // Tiny toast for feedback (subtle)
      try { toast.success(`✨ ${match.phrase.trigger} → expanderat`, { duration: 1200 }); } catch {/**/}
    };

    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('phrases-changed', reload);
      window.removeEventListener('phrases-enabled-changed', onEnabledChange);
    };
  }, []);
  return null;
}
